import React, { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Configuration
const API_ENDPOINT = 'https://36tbz5z6jh.execute-api.eu-central-1.amazonaws.com/prod';
const AUDIO_API_ENDPOINT = 'https://36tbz5z6jh.execute-api.eu-central-1.amazonaws.com/prod/audio';

const PhotoUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [status, setStatus] = useState('');
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [transcribedText, setTranscribedText] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Detect if user is on mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setExtractedText('');
      setStatus('');
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }
      });
      videoRef.current.srcObject = stream;
      setCameraActive(true);
    } catch (error) {
      setStatus('Camera access denied. Please use file upload instead.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
    setCameraActive(false);
  };

  // Capture photo
  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], `letter-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      stopCamera();
    }, 'image/jpeg', 0.8);
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use a format compatible with iOS Safari
      let options = { mimeType: 'audio/webm' };
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        // iOS Safari fallback
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          options = { mimeType: 'audio/mp4' };
        } else {
          options = {}; // Let browser choose
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        console.log('Recorded audio mimeType:', mimeType);
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        
        // Create object URL for playback
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        console.log('Audio blob size:', blob.size, 'bytes');
        console.log('Audio URL created:', url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setStatus('🎤 Recording audio...');
    } catch (error) {
      setStatus('❌ Microphone access denied. Please allow microphone access.');
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setStatus('✅ Recording stopped');
    }
  };

  // Process audio with AWS Transcribe
  const processAudio = async () => {
    if (!audioBlob) return;

    setProcessing(true);
    setStatus('📤 Uploading audio...');

    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      
      const audioData = await base64Promise;
      const fileName = `audio/${Date.now()}-recording.webm`;
      const bucketName = 'santa-ops-letters-originals-demo2026';

      // Start transcription job
      setStatus('🚀 Starting transcription job...');
      const startResponse = await fetch(AUDIO_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bucketName,
          fileName,
          audioData,
          contentType: 'audio/webm'
        })
      });

      const startResult = await startResponse.json();
      
      if (!startResult.success) {
        throw new Error(startResult.error || 'Failed to start transcription');
      }

      const jobName = startResult.jobName;
      setStatus(`🔄 Transcribing audio (Job: ${jobName})...`);

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 60; // 60 attempts = 2 minutes max
      const pollInterval = 2000; // Poll every 2 seconds

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        const statusResponse = await fetch(
          `${AUDIO_API_ENDPOINT}?jobName=${encodeURIComponent(jobName)}&bucketName=${encodeURIComponent(bucketName)}&fileName=${encodeURIComponent(fileName)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        const statusResult = await statusResponse.json();
        
        if (statusResult.status === 'COMPLETED') {
          setTranscribedText(statusResult.transcribedText);
          setStatus('✅ Transcription complete!');
          break;
        } else if (statusResult.status === 'FAILED') {
          throw new Error(statusResult.error || 'Transcription failed');
        } else {
          setStatus(`🔄 Transcribing... (${attempts * 2}s elapsed)`);
        }
      }

      if (attempts >= maxAttempts) {
        throw new Error('Transcription timeout - please try again');
      }

    } catch (error) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Upload and process - simplified version for demo
  const processPhoto = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setStatus('📤 Uploading image...');

    try {
      // For demo: we'll show a mock response since we need AWS credentials for real upload
      setUploading(false);
      setProcessing(true);
      setStatus('🔄 Processing with Textract...');

      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock extracted text for demo
      const mockText = `Dear Santa,

I have been very good this year. I would like:
- A new bicycle
- Some books about dinosaurs  
- A toy robot

Thank you!
Love, Emma

P.S. I left cookies for you!`;

      setExtractedText(mockText);
      setStatus('✅ Processing complete! (Demo mode - showing mock text)');

    } catch (error) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{ 
      maxWidth: '600px', 
      margin: '0 auto', 
      padding: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
      color: '#333'
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>
        📝 Child's Letter to Santa
      </h2>
      
      <p style={{ textAlign: 'center', marginBottom: '20px', fontSize: '0.95rem' }}>
        Choose how to capture the letter: photo, file, or voice recording
      </p>
      
      {/* Camera Section */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        {!cameraActive ? (
          <button 
            onClick={startCamera} 
            style={{ 
              padding: '15px 30px', 
              margin: '10px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1.1rem',
              cursor: 'pointer'
            }}
          >
            📷 Take Photo
          </button>
        ) : (
          <div>
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              style={{ 
                width: '100%', 
                maxWidth: '400px',
                borderRadius: '15px',
                marginBottom: '15px'
              }} 
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div>
              <button 
                onClick={capturePhoto} 
                style={{ 
                  padding: '12px 25px', 
                  margin: '5px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer'
                }}
              >
                📸 Capture
              </button>
              <button 
                onClick={stopCamera} 
                style={{ 
                  padding: '12px 25px', 
                  margin: '5px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer'
                }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', margin: '20px 0', fontWeight: 'bold' }}>OR</div>

      {/* File Upload */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
        <button 
          onClick={() => fileInputRef.current.click()}
          style={{ 
            padding: '15px 30px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            fontSize: '1.1rem',
            cursor: 'pointer'
          }}
        >
          📁 Choose File
        </button>
      </div>

      <div style={{ textAlign: 'center', margin: '20px 0', fontWeight: 'bold' }}>OR</div>

      {/* Audio Recording */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        {!isRecording ? (
          <button 
            onClick={startRecording}
            disabled={processing}
            style={{ 
              padding: '15px 30px',
              backgroundColor: processing ? '#bdc3c7' : '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1.1rem',
              cursor: processing ? 'not-allowed' : 'pointer'
            }}
          >
            🎤 Record Audio
          </button>
        ) : (
          <button 
            onClick={stopRecording}
            style={{ 
              padding: '15px 30px',
              backgroundColor: '#c0392b',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1.1rem',
              cursor: 'pointer',
              animation: 'pulse 1.5s infinite'
            }}
          >
            ⏹️ Stop Recording
          </button>
        )}
      </div>

      {/* Audio Preview */}
      {audioURL && (
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h3>🎵 Your Recording:</h3>
          
          {/* Show audio player only on desktop */}
          {!isMobile && (
            <audio 
              controls 
              src={audioURL}
              style={{ 
                width: '100%',
                maxWidth: '400px',
                marginBottom: '15px'
              }}
            />
          )}
          
          {/* Show success message on mobile */}
          {isMobile && (
            <div style={{ 
              padding: '20px',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '10px',
              marginBottom: '15px',
              color: '#155724'
            }}>
              ✅ Audio recorded successfully!
            </div>
          )}
          
          <div style={{ marginBottom: '15px' }}>
            <button 
              onClick={() => {
                setAudioBlob(null);
                setAudioURL(null);
                setTranscribedText('');
              }}
              style={{ 
                padding: '10px 20px',
                margin: '5px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              🔄 Re-record
            </button>
          </div>
          <button 
            onClick={processAudio}
            disabled={processing}
            style={{ 
              padding: '15px 30px',
              backgroundColor: processing ? '#bdc3c7' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1.1rem',
              cursor: processing ? 'not-allowed' : 'pointer'
            }}
          >
            {processing ? '🔄 Transcribing...' : '🚀 Transcribe Audio'}
          </button>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <h3>Preview:</h3>
          <img 
            src={preview} 
            alt="Preview" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '300px',
              borderRadius: '15px',
              marginBottom: '15px'
            }} 
          />
          <br />
          <button 
            onClick={processPhoto} 
            disabled={uploading || processing}
            style={{ 
              padding: '15px 30px',
              backgroundColor: processing ? '#bdc3c7' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1.1rem',
              cursor: processing ? 'not-allowed' : 'pointer'
            }}
          >
            {processing ? '🔄 Processing...' : '🚀 Process Letter'}
          </button>
        </div>
      )}

      {/* Status */}
      {status && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#ecf0f1', 
          borderRadius: '10px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {status}
        </div>
      )}

      {/* Extracted Text from Image */}
      {extractedText && (
        <div style={{ marginTop: '20px' }}>
          <h3>📄 Extracted Text from Image:</h3>
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            lineHeight: '1.5',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {extractedText}
          </div>
        </div>
      )}

      {/* Transcribed Text from Audio */}
      {transcribedText && (
        <div style={{ marginTop: '20px' }}>
          <h3>🎤 Transcribed Text from Audio:</h3>
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '10px',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            lineHeight: '1.5',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {transcribedText}
          </div>
        </div>
      )}

      {/* Configuration Notice */}
      <div style={{
        marginTop: '30px',
        padding: '15px',
        backgroundColor: '#d1ecf1',
        border: '1px solid #bee5eb',
        borderRadius: '10px',
        fontSize: '0.9rem'
      }}>
        <strong>🎤 Audio Transcription:</strong><br/>
        Audio recording uses AWS Transcribe for real-time speech-to-text conversion.
        <br/><br/>
        <strong>📷 Image Processing:</strong><br/>
        Image processing is in demo mode. For real AWS Textract processing, additional configuration is needed.
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default PhotoUpload;